import { useState, useCallback } from 'react';
import { message } from 'antd';
import axios from '../../../config/axiosInstance';

/**
 * useDeviceConfigData({ admin: boolean })
 * - admin = true  → GET /events/admin
 * - admin = false → GET /events
 */
const useDeviceConfigData = ({ admin = false } = {}) => {
    const [events, setEvents] = useState([]);
    const [programmePoints, setProgrammePoints] = useState([]);
    const [locations, setLocations] = useState([]);
    const [participantTypes, setParticipantTypes] = useState([]);
    const [customFields, setCustomFields] = useState([]);

    const fetchEvents = useCallback(async () => {
        const url = admin ? '/events/admin' : '/events';
        try {
            const { data } = await axios.get(url);
            setEvents(data);
        } catch {
            message.error('Failed to load events');
        }
    }, [admin]);

    const fetchToken = async (host) => {
        try {
            const tokenResponse = await axios.get('/regasus/token', { params: { host } });
            return tokenResponse.data.access_token;
        } catch (error) {
            message.error('Error connecting with Regasus');
            return null;
        }
    };

    const fetchEventDetails = useCallback(async (eventId, token, host) => {
        const dataFetchers = [
            {
                url: `/regasus/event-pps/${eventId}`,
                params: { token, host },
                setter: setProgrammePoints,
                dataKey: 'programmePoints.programmePoint',
            },
            {
                url: `/regasus/event-locations/${eventId}`,
                params: { token, host },
                setter: setLocations,
                dataKey: 'locations.location',
            },
            {
                url: `/regasus/event-participant-types/${eventId}`,
                params: { token, host },
                setter: setParticipantTypes,
                dataKey: 'participantTypes.participantType',
            },
            {
                url: `/regasus/event-custom-field-groups/${eventId}`,
                params: { token, host },
                setter: (data) => setCustomFields(data || []),
                dataKey: null,
            },
        ];

        await Promise.all(
            dataFetchers.map(async ({ url, params, setter, dataKey }) => {
                try {
                    const response = await axios.get(url, { params });
                    const data = dataKey
                        ? dataKey.split('.').reduce((obj, key) => obj?.[key] ?? null, response.data)
                        : response.data;
                    setter(data || []);
                } catch (error) {
                    setter([]);
                }
            })
        );
    }, []);

    return {
        events,
        programmePoints,
        locations,
        participantTypes,
        customFields,
        fetchEvents,
        fetchEventDetails,
        fetchToken,
        setProgrammePoints,
        setLocations,
        setParticipantTypes,
        setCustomFields,
    };
};

export default useDeviceConfigData;